import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useStore } from "store/zustand";
import { useGlobalLoader } from "contexts/GlobalLoaderContext";
import { GoogleAuthProvider, OAuthProvider, getAuth } from "firebase/auth";
import { signInWithProvider } from "utils/authFunctions";
import {
  handleSendCode,
  completeSignupIfVerified,
  resendVerificationEmail,
  cancelPendingSignup,
} from "./SendCodes";
import Button from "../../components/Button";
import "../../assets/css/style.css";
import "../../assets/icons/style.css";
import RegisterBg from "../../assets/images/Register.png";
import StandbyIcon from "../../assets/images/standby.png";

const Signup: React.FC = () => {
  const { referralCode } = useParams<{ referralCode: string }>();
  const { setIsAuthenticated } = useStore();
  const { showLoader, hideLoader } = useGlobalLoader();
  const [username, setUsername] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [isVerified, setIsVerified] = useState<boolean | null>(null);
  const [isEmailVerifying, setIsEmailVerifying] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false); // Local loading state
  // "Waiting for the user to click the email link" step, shown after the
  // verification email is sent (replaces the old blocking poll).
  const [awaitingVerification, setAwaitingVerification] =
    useState<boolean>(false);
  const [isChecking, setIsChecking] = useState<boolean>(false);
  // Seconds left before "Resend email" is allowed again (avoids Firebase's
  // auth/too-many-requests from rapid taps).
  const [resendCooldown, setResendCooldown] = useState<number>(0);

  // Start the cooldown as soon as we enter the waiting step (a link was just
  // sent), and reset it when leaving.
  useEffect(() => {
    setResendCooldown(awaitingVerification ? 60 : 0);
  }, [awaitingVerification]);

  // Tick the cooldown down once per second.
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const passwordRef = useRef<HTMLInputElement>(null);
  const confirmPasswordRef = useRef<HTMLInputElement>(null);
  // Set true once account creation starts, so the manual button + the three
  // auto-detect triggers can't create the account more than once.
  const completingRef = useRef<boolean>(false);

  // iOS leaves the last character revealed when -webkit-text-security is toggled
  // back on (eye off). Forcing a reflow makes WebKit repaint every glyph as a dot.
  const forceMaskRepaint = (el: HTMLInputElement | null) => {
    if (!el) return;
    const prev = el.style.display;
    el.style.display = "none";
    void el.offsetHeight; // trigger reflow
    el.style.display = prev;
  };

  useEffect(() => {
    forceMaskRepaint(passwordRef.current);
  }, [showPassword]);

  useEffect(() => {
    forceMaskRepaint(confirmPasswordRef.current);
  }, [showConfirmPassword]);
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: boolean }>(
    {},
  );
  const googleProvider = new GoogleAuthProvider();
  const appleProvider = new OAuthProvider("apple.com");
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState<boolean>(
    typeof window !== "undefined" ? window.innerWidth < 1024 : false,
  );
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const auth = getAuth();

  // Helper function to remove specific error messages from error string
  const removeErrorMessages = (
    currentError: string,
    messagesToRemove: string[],
  ): string => {
    if (!currentError) return "";
    const currentErrors = currentError.split("\n");
    const filteredErrors = currentErrors.filter(
      (err) => !messagesToRemove.some((msg) => err.includes(msg)),
    );
    return filteredErrors.join("\n");
  };

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Strictly prevent input beyond 20 characters
    if (value.length > 20) {
      setFieldErrors({ ...fieldErrors, username: true });
      const currentErrors = error ? error.split("\n") : [];
      const newErrors = currentErrors.filter(
        (err) => !err.includes("Username must be 20 characters or less"),
      );
      newErrors.push("Username must be 20 characters or less");
      setError(newErrors.join("\n"));
      // Don't update state if it exceeds 20
      return;
    }
    // Clear errors if username is valid
    const errorsToRemove: string[] = [];
    if (value.length > 0) {
      errorsToRemove.push("Username is required");
    }
    if (value.length <= 20) {
      errorsToRemove.push("Username must be 20 characters or less");
    }

    if (errorsToRemove.length > 0) {
      const newError = removeErrorMessages(error, errorsToRemove);
      setError(newError);
      if (value.length > 0 && value.length <= 20) {
        setFieldErrors({ ...fieldErrors, username: false });
      }
    }
    setUsername(value);
  };

  const handleUsernameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;
    // Prevent typing if already at 20 characters (allow deletion and navigation keys)
    if (
      target.value.length >= 20 &&
      e.key !== "Backspace" &&
      e.key !== "Delete" &&
      e.key !== "ArrowLeft" &&
      e.key !== "ArrowRight" &&
      e.key !== "Tab" &&
      e.key !== "Home" &&
      e.key !== "End" &&
      !e.ctrlKey &&
      !e.metaKey &&
      !e.altKey
    ) {
      e.preventDefault();
      setFieldErrors({ ...fieldErrors, username: true });
      const currentErrors = error ? error.split("\n") : [];
      const newErrors = currentErrors.filter(
        (err) => !err.includes("Username must be 20 characters or less"),
      );
      newErrors.push("Username must be 20 characters or less");
      setError(newErrors.join("\n"));
    }
  };

  const handleEmail = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);

    // Clear email-related errors when email becomes valid
    const errorsToRemove: string[] = [];
    if (value.length > 0) {
      errorsToRemove.push("Email is required");
    }
    if (value.length > 0 && isEmail(value)) {
      errorsToRemove.push("Please enter a valid email");
      setFieldErrors({ ...fieldErrors, email: false });
    }

    if (errorsToRemove.length > 0) {
      const newError = removeErrorMessages(error, errorsToRemove);
      setError(newError);
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    let newPassword = value;

    if (value.length > 20) {
      newPassword = value.substring(0, 20);
      const currentErrors = error ? error.split("\n") : [];
      const newErrors = currentErrors.filter(
        (err) => !err.includes("Password must be 20 characters or less"),
      );
      newErrors.push("Password must be 20 characters or less");
      setError(newErrors.join("\n"));
      setPassword(newPassword);
      return;
    }

    setPassword(newPassword);

    // Clear password-related errors when password becomes valid
    const errorsToRemove: string[] = [];
    if (newPassword.length > 0) {
      errorsToRemove.push("Password is required");
    }
    if (newPassword.length <= 20) {
      errorsToRemove.push("Password must be 20 characters or less");
    }
    if (newPassword.length >= 8) {
      errorsToRemove.push("Password must be at least 8 characters long");
    }
    if (newPassword.length > 0 && /[A-Z]/.test(newPassword)) {
      errorsToRemove.push(
        "Password must contain at least one uppercase letter",
      );
    }
    if (newPassword.length > 0 && /[a-z]/.test(newPassword)) {
      errorsToRemove.push(
        "Password must contain at least one lowercase letter",
      );
    }
    if (newPassword.length > 0 && /\d/.test(newPassword)) {
      errorsToRemove.push("Password must contain at least one number");
    }
    if (
      newPassword.length > 0 &&
      /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(newPassword)
    ) {
      errorsToRemove.push(
        "Password must contain at least one special character",
      );
    }

    // Check if password matches confirm password
    if (
      newPassword.length > 0 &&
      confirmPassword &&
      newPassword === confirmPassword
    ) {
      errorsToRemove.push("Passwords do not match");
      setFieldErrors({ ...fieldErrors, confirmPassword: false });
    }

    if (errorsToRemove.length > 0) {
      const newError = removeErrorMessages(error, errorsToRemove);
      setError(newError);

      // Update fieldErrors if password is valid
      if (
        newPassword.length >= 8 &&
        /[A-Z]/.test(newPassword) &&
        /[a-z]/.test(newPassword) &&
        /\d/.test(newPassword) &&
        /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(newPassword) &&
        newPassword.length <= 20
      ) {
        setFieldErrors({ ...fieldErrors, password: false });
      }
    }
  };
  const handleConfirmPasswordChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = e.target.value;
    setConfirmPassword(value);

    // Clear confirm password-related errors when passwords match
    const errorsToRemove: string[] = [];
    if (value.length > 0) {
      errorsToRemove.push("Confirm password is required");
    }
    if (value.length > 0 && password && value === password) {
      errorsToRemove.push("Passwords do not match");
      setFieldErrors({ ...fieldErrors, confirmPassword: false });
    }

    if (errorsToRemove.length > 0) {
      const newError = removeErrorMessages(error, errorsToRemove);
      setError(newError);
    }
  };

  const isEmail = (input: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input);
  const isStrongPassword = (password: string) => {
    return /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]).{8,}$/.test(
      password,
    );
  };

  const validateForm = () => {
    const errors: string[] = [];
    const fieldErrors: { [key: string]: boolean } = {};

    if (!username) {
      errors.push("Username is required");
      fieldErrors.username = true;
    }
    if (username && username.length > 20) {
      errors.push("Username must be 20 characters or less");
      fieldErrors.username = true;
    }
    if (!email) {
      errors.push("Email is required");
      fieldErrors.email = true;
    }
    if (!password) {
      errors.push("Password is required");
      fieldErrors.password = true;
    }
    if (!confirmPassword) {
      errors.push("Confirm password is required");
      fieldErrors.confirmPassword = true;
    }
    if (email && !isEmail(email)) errors.push("Please enter a valid email");
    if (password && password.length < 8)
      errors.push("Password must be at least 8 characters long");
    if (password && !/[A-Z]/.test(password))
      errors.push("Password must contain at least one uppercase letter");
    if (password && !/[a-z]/.test(password))
      errors.push("Password must contain at least one lowercase letter");
    if (password && !/\d/.test(password))
      errors.push("Password must contain at least one number");
    if (password && !/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password))
      errors.push("Password must contain at least one special character");
    if (password && confirmPassword && password !== confirmPassword)
      errors.push("Passwords do not match");

    setFieldErrors(fieldErrors);
    setError(errors.join("\n"));
    return errors.length === 0;
  };

  // Navigate, preserving the referral code when present.
  const navigateWithReferral = (path: string) =>
    navigate(
      referralCode
        ? `${path}?referral=${encodeURIComponent(referralCode)}`
        : path,
    );

  const handleSendCodeWrapper = async () => {
    if (!validateForm()) return;

    completingRef.current = false; // fresh signup can complete again
    try {
      await handleSendCode(
        email,
        username,
        password,
        setError,
        setIsEmailVerifying,
        navigateWithReferral,
        setIsVerified,
        setIsAuthenticated,
        setAwaitingVerification,
        referralCode,
      );
    } catch (error: any) {
      setError(error.message || "Failed to send verification code.");
    }
  };

  // "I've verified my email" — check Firebase and finish creating the account.
  const handleConfirmVerified = () =>
    completeSignupIfVerified(setError, navigateWithReferral, setIsChecking, {
      guard: completingRef,
    });

  // Auto-detect verification while on the waiting step: the user typically
  // clicks the link in another tab / their email app, so re-check whenever they
  // return to this tab (focus / visibility) — plus a gentle background poll as a
  // fallback. All silent (no error/spinner if not verified yet); the manual
  // button stays as a backup. Non-blocking, no timeout, nothing is deleted.
  useEffect(() => {
    if (!awaitingVerification) return;
    const check = () =>
      completeSignupIfVerified(setError, navigateWithReferral, setIsChecking, {
        silent: true,
        guard: completingRef,
      });
    const onVisible = () => {
      if (document.visibilityState === "visible") check();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", check);
    const interval = setInterval(check, 5000);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", check);
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [awaitingVerification]);

  // "Resend email" — gated by the cooldown. Clears any stale error first, and
  // gates immediately so rapid taps can't trigger auth/too-many-requests.
  const handleResendVerification = async () => {
    if (resendCooldown > 0) return;
    setError("");
    setResendCooldown(60);
    await resendVerificationEmail(setError);
  };

  // "Use a different email" — drop the pending (unverified) account and go back
  // to the form.
  const handleUseDifferentEmail = async () => {
    await cancelPendingSignup();
    setAwaitingVerification(false);
    completingRef.current = false; // fresh attempt can complete again
    setError("");
    // They explicitly want a DIFFERENT email, so clear it (and its field error)
    // rather than leaving the old one pre-filled. Name/password are kept for
    // convenience since they'll likely reuse them.
    setEmail("");
    setFieldErrors((prev) => ({ ...prev, email: false }));
  };

  const signInWithGoogle = async () => {
    setIsLoading(true);
    showLoader();
    try {
      const userOrError = await signInWithProvider(
        googleProvider,
        setIsAuthenticated,
        navigate,
        true,
      ); // Pass isSignup: true
      if (typeof userOrError === "string") {
        setError(userOrError);
      }
    } catch (error: any) {
      setError("Google sign-up failed. Please try again.");
    } finally {
      hideLoader();
      setIsLoading(false);
    }
  };

  const signInWithApple = async () => {
    setIsLoading(true);
    showLoader();
    try {
      const userOrError = await signInWithProvider(
        appleProvider,
        setIsAuthenticated,
        navigate,
        true,
      ); // Pass isSignup: true
      if (typeof userOrError === "string") {
        setError(userOrError);
      }
    } catch (error: any) {
      setError("Apple sign-up failed. Please try again.");
    } finally {
      hideLoader();
      setIsLoading(false);
    }
  };

  const handleSubmit = () => {
    if (validateForm()) {
      handleSendCodeWrapper();
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 50 }}>
      {/* Full-page Background Image */}
      <img
        src={RegisterBg}
        alt="Register background"
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
        }}
      />
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundColor: "rgba(0,0,0,0.35)",
        }}
      />

      {/* Content Layer */}
      <div
        style={{
          position: "relative",
          zIndex: 10,
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          width: "100%",
          height: "100%",
          overflowY: isMobile ? "auto" : "hidden",
        }}
      >
        {/* Left - Logo & Tagline */}
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            padding: isMobile ? "1.5rem" : "2.5rem",
          }}
        >
          <div className="flex items-center gap-2">
            <img src={StandbyIcon} alt="Standby" className="w-8 h-8" />
            <span className="text-white text-xl font-bold tracking-wide">
              STANDBY
            </span>
          </div>
          {!isMobile && (
            <div className="mb-10">
              <h1 className="text-white text-4xl font-bold leading-tight">
                Connecting <span className="text-light-yellow">Air Crews,</span>
                <br />
                <span className="italic">Seamlessly.</span>
              </h1>
            </div>
          )}
        </div>

        {/* Right - Form Card */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: isMobile ? "1rem" : "2rem",
            minWidth: isMobile ? 0 : "640px",
            maxWidth: isMobile ? "100%" : "720px",
            width: isMobile ? "100%" : undefined,
          }}
        >
          <div
            className="w-full"
            style={{
              maxWidth: isMobile ? "28rem" : undefined,
              marginLeft: isMobile ? "auto" : undefined,
              marginRight: isMobile ? "auto" : undefined,
            }}
          >
            <div
              className={
                isMobile
                  ? "bg-white rounded-2xl shadow-lg px-6 py-10 overflow-y-auto"
                  : "bg-white rounded-2xl shadow-lg px-12 py-16 sm:px-14 sm:py-20 overflow-y-auto"
              }
              style={{ maxHeight: isMobile ? "none" : "95vh" }}
            >
              <div className="flex justify-center">
                <span className="inline-block px-4 py-1 rounded-full border border-gray-200 text-xs text-grease-100">
                  Step 1 of 3
                </span>
              </div>
              <h2 className="text-2xl font-bold text-grease-100 text-center mt-4">
                Sign Up To Continue
              </h2>
              <p className="text-sm mt-2 font-normal text-grease-50 text-center">
                Get a registration code via email.
              </p>

              <div className="mt-8">
                <input
                  type="text"
                  name="new-username"
                  id="username"
                  autoComplete="off"
                  value={username}
                  onChange={handleUsernameChange}
                  onKeyDown={handleUsernameKeyDown}
                  onPaste={(e) => {
                    e.preventDefault();
                    const pastedText = e.clipboardData.getData("text");
                    const newValue = (username + pastedText).substring(0, 20);
                    const errorsToRemove: string[] = [];

                    if (
                      newValue.length >= 20 &&
                      (username + pastedText).length > 20
                    ) {
                      setFieldErrors({ ...fieldErrors, username: true });
                      const currentErrors = error ? error.split("\n") : [];
                      const newErrors = currentErrors.filter(
                        (err) =>
                          !err.includes(
                            "Username must be 20 characters or less",
                          ),
                      );
                      newErrors.push("Username must be 20 characters or less");
                      setError(newErrors.join("\n"));
                    } else {
                      if (newValue.length > 0) {
                        errorsToRemove.push("Username is required");
                      }
                      if (newValue.length <= 20) {
                        errorsToRemove.push(
                          "Username must be 20 characters or less",
                        );
                      }
                      if (errorsToRemove.length > 0) {
                        const newError = removeErrorMessages(
                          error,
                          errorsToRemove,
                        );
                        setError(newError);
                      }
                      setFieldErrors({ ...fieldErrors, username: false });
                    }
                    setUsername(newValue);
                  }}
                  onInput={(e) => {
                    const target = e.target as HTMLInputElement;
                    if (target.value.length > 20) {
                      target.value = target.value.substring(0, 20);
                      setFieldErrors({ ...fieldErrors, username: true });
                      const currentErrors = error ? error.split("\n") : [];
                      const newErrors = currentErrors.filter(
                        (err) =>
                          !err.includes(
                            "Username must be 20 characters or less",
                          ),
                      );
                      newErrors.push("Username must be 20 characters or less");
                      setError(newErrors.join("\n"));
                      setUsername(target.value);
                    } else {
                      const errorsToRemove: string[] = [];
                      if (target.value.length > 0) {
                        errorsToRemove.push("Username is required");
                      }
                      if (target.value.length <= 20) {
                        errorsToRemove.push(
                          "Username must be 20 characters or less",
                        );
                      }
                      if (errorsToRemove.length > 0) {
                        const newError = removeErrorMessages(
                          error,
                          errorsToRemove,
                        );
                        setError(newError);
                        setFieldErrors({ ...fieldErrors, username: false });
                      }
                    }
                  }}
                  maxLength={20}
                  className={`block w-full p-3.5 rounded-lg bg-white text-sm text-grease-100 placeholder-gray-400
                  border border-gray-200
                  focus:border-light-yellow
                  focus:outline-none
                  focus:ring-1
                  focus:ring-light-yellow
                  focus:ring-offset-0
                  ${fieldErrors.username ? " !border-red-500" : ""}`}
                  placeholder="Name"
                />
                <input
                  type="email"
                  name="new-email"
                  id="email"
                  value={email}
                  autoComplete="new-email"
                  onChange={handleEmail}
                  className={`block mt-4 w-full p-3.5 rounded-lg bg-white text-sm text-grease-100 placeholder-gray-400
                  border border-gray-200
                  focus:border-light-yellow
                  focus:outline-none
                  focus:ring-1
                  focus:ring-light-yellow
                  focus:ring-offset-0
                  ${fieldErrors.email ? " !border-red-500" : ""}`}
                  placeholder="Email"
                />
                <div className="relative mt-4">
                  <input
                    ref={passwordRef}
                    type="text"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    name="password"
                    id="password"
                    value={password}
                    onChange={handlePasswordChange}
                    onKeyDown={(e) => {
                      if (
                        password.length >= 20 &&
                        e.key !== "Backspace" &&
                        e.key !== "Delete" &&
                        e.key !== "ArrowLeft" &&
                        e.key !== "ArrowRight" &&
                        e.key !== "Tab" &&
                        e.key !== "Home" &&
                        e.key !== "End" &&
                        !e.ctrlKey &&
                        !e.metaKey &&
                        !e.altKey
                      ) {
                        e.preventDefault();
                        const currentErrors = error ? error.split("\n") : [];
                        const newErrors = currentErrors.filter(
                          (err) =>
                            !err.includes(
                              "Password must be 20 characters or less",
                            ),
                        );
                        newErrors.push(
                          "Password must be 20 characters or less",
                        );
                        setError(newErrors.join("\n"));
                      }
                    }}
                    minLength={6}
                    maxLength={20}
                    className={`pw-field ${showPassword ? "" : "pw-mask"} block w-full p-3.5 rounded-lg bg-white text-sm text-grease-100 placeholder-gray-400 pr-10
                    border border-gray-200
                    focus:border-light-yellow
                    focus:outline-none
                    focus:ring-1
                    focus:ring-light-yellow
                    focus:ring-offset-0
                    ${fieldErrors.password ? " !border-red-500" : ""}`}
                    placeholder="Password"
                    autoComplete="new-password"
                    data-lpignore="true"
                    aria-describedby={
                      error.includes("Password") ? "password-error" : undefined
                    }
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                    aria-label={
                      showPassword ? "Hide password" : "Show password"
                    }
                  >
                    <svg
                      className={`shrink-0 size-3.5 ${showPassword ? "hs-password-active" : ""}`}
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path
                        className={showPassword ? "hidden" : ""}
                        d="M9.88 9.88a3 3 0 1 0 4.24 4.24"
                      ></path>
                      <path
                        className={showPassword ? "hidden" : ""}
                        d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68"
                      ></path>
                      <path
                        className={showPassword ? "hidden" : ""}
                        d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61"
                      ></path>
                      <line
                        className={showPassword ? "hidden" : ""}
                        x1="2"
                        x2="22"
                        y1="2"
                        y2="22"
                      ></line>
                      <path
                        className={showPassword ? "" : "hidden"}
                        d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"
                      ></path>
                      <circle
                        className={showPassword ? "" : "hidden"}
                        cx="12"
                        cy="12"
                        r="3"
                      ></circle>
                    </svg>
                  </button>
                </div>
                <div className="relative mt-4">
                  <input
                    ref={confirmPasswordRef}
                    type="text"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    name="confirmPassword"
                    id="confirmPassword"
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={handleConfirmPasswordChange}
                    data-lpignore="true"
                    className={`pw-field ${showConfirmPassword ? "" : "pw-mask"} block w-full p-3.5 rounded-lg bg-white text-sm text-grease-100 placeholder-gray-400
                    border border-gray-200
                    focus:border-light-yellow
                    focus:outline-none
                    focus:ring-1
                    focus:ring-light-yellow
                    focus:ring-offset-0
                    ${fieldErrors.confirmPassword ? " !border-red-500" : ""}`}
                    placeholder="Confirm Password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                    aria-label={
                      showConfirmPassword
                        ? "Hide confirm password"
                        : "Show confirm password"
                    }
                  >
                    <svg
                      className={`shrink-0 size-3.5 ${showConfirmPassword ? "hs-password-active" : ""}`}
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path
                        className={showConfirmPassword ? "hidden" : ""}
                        d="M9.88 9.88a3 3 0 1 0 4.24 4.24"
                      ></path>
                      <path
                        className={showConfirmPassword ? "hidden" : ""}
                        d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68"
                      ></path>
                      <path
                        className={showConfirmPassword ? "hidden" : ""}
                        d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61"
                      ></path>
                      <line
                        className={showConfirmPassword ? "hidden" : ""}
                        x1="2"
                        x2="22"
                        y1="2"
                        y2="22"
                      ></line>
                      <path
                        className={showConfirmPassword ? "" : "hidden"}
                        d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"
                      ></path>
                      <circle
                        className={showConfirmPassword ? "" : "hidden"}
                        cx="12"
                        cy="12"
                        r="3"
                      ></circle>
                    </svg>
                  </button>
                </div>
              </div>

              {error &&
                error.split("\n").map((err, idx) => (
                  <p key={idx} className="text-red-500 mt-2 text-sm">
                    {err}
                  </p>
                ))}
              <div id="recaptcha-container"></div>
              {awaitingVerification ? (
                <div className="mt-6 rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <p className="text-sm text-grease-100 font-medium">
                    We sent a verification link to{" "}
                    <span className="font-semibold break-all">{email}</span>.
                  </p>
                  <p className="text-xs text-grease-50 mt-1">
                    Open the email and click the link — we'll detect it
                    automatically when you come back. (Or tap below once you've
                    verified.)
                  </p>
                  <Button
                    onClick={handleConfirmVerified}
                    disabled={isChecking}
                    className="w-full p-3.5 rounded-lg text-[#192227] text-center font-semibold shadow-button bg-light-yellow text-sm mt-4 disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 transition-opacity"
                  >
                    {isChecking ? "Checking..." : "I've verified my email"}
                  </Button>
                  <div className="flex items-center justify-between mt-3">
                    <button
                      type="button"
                      onClick={handleResendVerification}
                      disabled={resendCooldown > 0}
                      className={`text-sm font-semibold ${
                        resendCooldown > 0
                          ? "text-grease-50 cursor-not-allowed"
                          : "text-light-yellow cursor-pointer"
                      }`}
                    >
                      {resendCooldown > 0
                        ? `Resend in ${resendCooldown}s`
                        : "Resend email"}
                    </button>
                    <button
                      type="button"
                      onClick={handleUseDifferentEmail}
                      className="text-sm text-grease-200 cursor-pointer hover:underline"
                    >
                      Use a different email
                    </button>
                  </div>
                </div>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={isLoading || isEmailVerifying}
                  className="w-full p-3.5 rounded-lg text-[#192227] text-center font-semibold shadow-button bg-light-yellow text-sm mt-6 disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 transition-opacity"
                >
                  {isLoading || isEmailVerifying
                    ? "Verifying..."
                    : "Verify My Mail Id"}
                </Button>
              )}
              <div className="flex items-center justify-center text-sm gap-1 mt-6">
                <p className="text-grease-200">Already have an account?</p>
                <a
                  className="text-light-yellow font-semibold cursor-pointer"
                  onClick={() => navigate("/login")}
                >
                  Sign In
                </a>
              </div>

              <div className="flex items-center gap-4 mt-6">
                <div className="w-full h-px bg-gray-200"></div>
                <p className="text-grease-50 text-sm">Or</p>
                <div className="w-full h-px bg-gray-200"></div>
              </div>

              <div
                className={`flex gap-3 mt-6 ${isMobile ? "flex-col" : "flex-row"}`}
              >
                <div
                  onClick={signInWithGoogle}
                  className="p-3 border border-gray-200 rounded-lg flex gap-2 items-center justify-center flex-1 min-w-0 cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <img
                    className="w-5 h-5 flex-shrink-0"
                    src="/images/_Google.svg"
                    alt="Google"
                  />
                  <p className="text-grease-100 text-sm font-medium whitespace-nowrap">
                    Sign In with Google
                  </p>
                </div>
                <div
                  onClick={signInWithApple}
                  className="p-3 border border-gray-200 rounded-lg flex gap-2 items-center justify-center flex-1 min-w-0 cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <img
                    className="w-5 h-5 flex-shrink-0"
                    src="/images/apple.svg"
                    alt="Apple"
                  />
                  <p className="text-grease-100 text-sm font-medium whitespace-nowrap">
                    Sign In with Apple
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
